import type {
  AccessRequestResponse,
  MeResponse,
  MembershipSummaryResponse,
  MyAccessRequestStatusResponse,
  Permission,
  PendingInvitationResponse,
  PendingPlatformInvitationResponse,
  SessionUserResponse,
  SubmitJoinAccessRequest,
  SubmitNewTenantAccessRequest,
  ValidatedIdentityResponse,
} from '@contract-analyzer/api-client';
import { createContext } from 'react';

import type { DevPersona } from '@/lib/session-auth';

type AuthUserIdentity = Pick<
  MeResponse,
  'created_at' | 'display_name' | 'email' | 'is_superadmin' | 'status' | 'user_audience' | 'user_id'
>;

type AuthUserSession = Pick<
  SessionUserResponse,
  'expires_at' | 'membership_id' | 'tenant_display_name' | 'tenant_id' | 'tenant_slug'
> & {
  readonly permissions: SessionUserResponse['tenant_permissions'];
};

export interface AuthUser extends AuthUserIdentity, AuthUserSession {
  readonly accepted_terms_at: string | null;
}

export type OnboardingIdentity = ValidatedIdentityResponse;

export interface AuthState {
  readonly availableMemberships: readonly MembershipSummaryResponse[];
  readonly error: string | null;
  readonly isAuthenticated: boolean;
  readonly isLoading: boolean;
  readonly onboardingIdentity: OnboardingIdentity | null;
  readonly onboardingRequest: AccessRequestResponse | null;
  readonly pendingInvitations: readonly PendingInvitationResponse[];
  readonly pendingPlatformInvitations: readonly PendingPlatformInvitationResponse[];
  readonly requiresOnboarding: boolean;
  readonly termsAccepted: boolean;
  readonly user: AuthUser | null;
  readonly hasPermission: (permission: Permission) => boolean;
  readonly login: (devPersona?: DevPersona) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly acceptTerms: () => Promise<void>;
  readonly refreshOnboardingRequest: () => Promise<MyAccessRequestStatusResponse>;
  readonly retryOnboardingBootstrap: () => Promise<void>;
  readonly submitJoinTenantRequest: (
    input: SubmitJoinAccessRequest,
  ) => Promise<MyAccessRequestStatusResponse>;
  readonly submitNewTenantRequest: (
    input: SubmitNewTenantAccessRequest,
  ) => Promise<MyAccessRequestStatusResponse>;
  readonly switchTenant: (tenantId: string) => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);
